import { useQuery } from '@tanstack/react-query';
import { SEMI_FRESH } from '../../lib/queryPresets';
import { CheckSquare, Flame } from 'lucide-react';
import { format, parseISO, isToday, isTomorrow, isPast } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Card } from '../ui/Card';
import { WIDGET_HEADER_FLUSH, WidgetCardHeader } from './WidgetCardHeader';
import { Spinner } from '../ui/Spinner';
import { getMyOpenTasks } from '../../services/tasks/taskService';
import { useAuthStore } from '../../store/useAuthStore';
import { useTaskModalStore } from '../../store/taskModalStore';
import { getPriorityMeta } from '../tasks/taskHelpers';
import { WidgetInfiniteList } from './WidgetInfiniteList';
import { revealSlice } from '../../services/dashboard/ordreDuJour';

function formatDeadline(d: string | null): { text: string; className: string } | null {
  if (!d) return null;
  const date = parseISO(d);
  if (isPast(date) && !isToday(date)) {
    return { text: 'En retard', className: 'text-cime-urgent font-medium' };
  }
  if (isToday(date)) return { text: "Aujourd'hui", className: 'text-orange-600 font-medium' };
  if (isTomorrow(date)) return { text: 'Demain', className: 'text-amber-600' };
  return { text: format(date, 'EEE d MMM', { locale: fr }), className: 'text-neutral-500' };
}

export function MyTasksWidget() {
  const collaborator = useAuthStore((s) => s.collaborator);
  const openTask = useTaskModalStore((s) => s.openTask);

  const { data: tasks, isLoading } = useQuery({
    ...SEMI_FRESH,
    queryKey: ['dashboard', 'my-open-tasks', collaborator?.id ?? ''],
    queryFn: () => getMyOpenTasks(collaborator!.id),
    enabled: !!collaborator?.id,
  });

  const allTasks = tasks ?? [];

  return (
    <Card
      className="h-full"
      headerClassName={WIDGET_HEADER_FLUSH}
      header={<WidgetCardHeader icon={CheckSquare} title="Mes tâches" href="/taches" />}
    >
      {isLoading ? (
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      ) : !tasks || tasks.length === 0 ? (
        <p className="text-sm text-neutral-400 text-center py-3">
          Aucune tâche en cours qui vous est assignée
        </p>
      ) : (
        <WidgetInfiniteList itemCount={allTasks.length}>
          {(visibleCount) => {
            const page = revealSlice(allTasks, visibleCount);
            return (
              <ul className="space-y-1.5">
                {page.items.map((t) => {
                  const deadline = formatDeadline(t.deadline ?? t.due_date);
                  return (
                    <li key={t.id}>
                      <button
                        type="button"
                        onClick={() => openTask(t.id)}
                        className="w-full flex items-center gap-2 text-left py-1 hover:bg-neutral-50 rounded px-1 transition-colors"
                      >
                        <span
                          className={`text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 ${getPriorityMeta(t.priority).className}`}
                        >
                          {getPriorityMeta(t.priority).label}
                        </span>
                        {t.priority === 1 && (
                          <Flame className="w-3 h-3 text-cime-urgent flex-shrink-0" aria-hidden />
                        )}
                        <span className="text-sm text-neutral-700 flex-1 truncate">{t.content}</span>
                        {deadline && (
                          <span className={`text-xs flex-shrink-0 ${deadline.className}`}>
                            {deadline.text}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            );
          }}
        </WidgetInfiniteList>
      )}
    </Card>
  );
}
